/**
 * Register Exceptions Config
 *
 * Covers register-operations exception codes:
 * - ex-outlet-ls-register-open-too-long
 * - ex-outlet-ls-register-prefix-incorrect
 * - ex-register-low-closure-rate
 *
 * The ``ex-register-closure-missing-in-xero`` check used to live here.
 * It moved to ``frontend/src/config/exceptions/accounting/`` because
 * its concern is the Xero ACCREC / ACCPAY journal-posting integrity,
 * not register operations.
 *
 * ``renderOutlet``, ``renderRegister``, and ``platformCommonColumns``
 * are exported so the moved closure-missing config can re-use them.
 */
import React from 'react'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import {
  Store as StoreIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  LockClock as LockClockIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import { handleExternalLinkClick } from '../../../utils/externalLinks'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  textPart,
} from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { INVALID_CELL_BG } from '../../../theme/exceptionTableStyles'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'

const VALID_PREFIX_REGEX = /^[A-Z]{3}[0-9]{1,2}-$/

export const renderOutlet = (value: any, row: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(StoreIcon, { fontSize: 'small', color: 'action' }),
    React.createElement(Typography, { variant: 'body2' }, value)
  )

export const renderRegister = (value: any, row: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(WarningIcon, { color: 'warning', fontSize: 'small' }),
    React.createElement(Typography, { variant: 'body2', fontWeight: 'medium' }, value)
  )

const renderClosureRate = (value: any, row: Record<string, any>): React.ReactNode => {
  const meta = row.exception_metadata
  if (!meta?.closure_rate && meta?.closure_rate !== 0) return '-'
  const pct = Math.round(meta.closure_rate * 100)
  const color: 'error' | 'warning' | 'default' = pct < 25 ? 'error' : pct < 50 ? 'warning' : 'default'
  return withTooltip(
    { title: `${meta.closure_days} of ${meta.effective_days} days with closure` },
    React.createElement(Chip, { label: `${pct}%`, color, size: 'small' })
  )
}

const renderStatus = (value: any, row: Record<string, any>): React.ReactNode => {
  if (row.is_open) {
    return React.createElement(Chip, statusPillChipProps('Open', 'success'))
  }
  return React.createElement(Chip, {
    label: 'Closed',
    size: 'small',
    variant: 'filled',
    sx: { backgroundColor: '#000000', color: '#ffffff', '&:hover': { backgroundColor: '#333333' } },
  })
}

const formatDaysOpen = (days: number): string => {
  if (days < 1) {
    const hours = Math.floor(days * 24)
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  return `${Math.floor(days)} day${Math.floor(days) !== 1 ? 's' : ''}`
}

const getDurationColor = (daysOpen: number, cutoffDays?: number): 'error' | 'warning' | 'default' => {
  const threshold = cutoffDays || 2
  if (daysOpen > threshold * 2) return 'error'
  if (daysOpen > threshold) return 'warning'
  return 'default'
}

const renderDuration = (value: any, row: Record<string, any>): React.ReactNode => {
  if (row.days_open === undefined) return '-'
  return React.createElement(Chip, {
    label: formatDaysOpen(row.days_open),
    color: getDurationColor(row.days_open, row.cutoff_days),
    size: 'small',
  })
}

const renderOpenTime = (value: any): React.ReactNode => {
  if (!value) return '-'
  return React.createElement(
    Typography,
    { variant: 'caption', color: 'text.secondary' },
    new Date(value).toLocaleString()
  )
}

const renderActions = (value: any, row: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', gap: 0.5, flexWrap: 'wrap' } },
    row.lightspeed_register_close_link &&
      withTooltip(
        { title: 'Close Register' },
        React.createElement(
          IconButton,
          {
            size: 'small',
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleExternalLinkClick(
                e as any,
                row.lightspeed_register_close_link,
                'Close Register'
              )
            },
            sx: { color: 'warning.main' },
          },
          React.createElement(LockClockIcon, { fontSize: 'medium' })
        )
      ),
    row.lightspeed_register_closure_link &&
      withTooltip(
        { title: 'View Closure History' },
        React.createElement(
          IconButton,
          {
            size: 'small',
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleExternalLinkClick(
                e as any,
                row.lightspeed_register_closure_link,
                'View Closure History'
              )
            },
            sx: { color: 'primary.main' },
          },
          React.createElement(HistoryIcon, { fontSize: 'medium' })
        )
      ),
    row.lightspeed_outlet_edit_link &&
      withTooltip(
        { title: 'Edit Outlet' },
        React.createElement(
          IconButton,
          {
            size: 'small',
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleExternalLinkClick(
                e as any,
                row.lightspeed_outlet_edit_link,
                'Edit Outlet'
              )
            },
            sx: { color: 'action.active' },
          },
          React.createElement(EditIcon, { fontSize: 'medium' })
        )
      )
  )

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'outlet_name',
    label: 'Outlet',
    contexts: ['inspector'],
    render: renderOutlet,
  },
  {
    key: 'register_name',
    label: 'Register',
    contexts: ['inspector'],
    render: renderRegister,
  },
  {
    key: 'invoice_prefix',
    label: 'Prefix',
    contexts: ['inspector'],
    render: (value) => value || '-',
    getCellStyle: (value) => {
      if (!value || !VALID_PREFIX_REGEX.test(value)) {
        return { backgroundColor: INVALID_CELL_BG }
      }
      return {}
    },
  },
  {
    key: 'closure_rate',
    label: 'Closure Rate (30d)',
    contexts: ['inspector'],
    render: renderClosureRate,
    getSortValue: (row) => row.exception_metadata?.closure_rate ?? -1,
  },
  {
    key: 'is_open',
    label: 'Status',
    contexts: ['inspector'],
    render: renderStatus,
  },
  {
    key: 'days_open',
    label: 'Open Duration',
    contexts: ['inspector'],
    render: renderDuration,
    getSortValue: (row) => row.days_open ?? 0,
  },
  {
    key: 'register_open_time',
    label: 'Open Time',
    contexts: ['inspector'],
    render: renderOpenTime,
    getSortValue: (row) => row.register_open_time || '',
  },
  {
    key: 'actions',
    label: 'Actions',
    contexts: ['inspector'],
    render: renderActions,
  },
]

export const platformCommonColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'outlet_name',
    label: 'Outlet',
    contexts: ['platform'],
  },
  {
    key: 'register_name',
    label: 'Register',
    contexts: ['platform'],
  },
]

const lowClosureRatePlatformColumns: ExceptionColumnDef[] = [
  ...platformCommonColumns,
  {
    key: 'closure_rate',
    label: 'Daily Close Rate',
    type: 'percentage',
    align: 'center',
    contexts: ['platform'],
    exportFormat: 'percentage',
  },
  {
    key: 'closure_days',
    label: 'Days Closed',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'effective_days',
    label: 'Effective Days',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
]

const openTooLongPlatformColumns: ExceptionColumnDef[] = [
  ...platformCommonColumns,
  {
    key: 'is_open',
    label: 'Register Open',
    align: 'center',
    contexts: ['platform'],
    render: (value) => (value ? 'Yes' : value === false ? 'No' : '-'),
  },
  {
    key: 'days_open',
    label: 'Days Open',
    align: 'right',
    contexts: ['platform'],
    render: (value) => {
      if (value === null || value === undefined) return '-'
      const days = Number(value)
      return days < 1 ? `${Math.floor(days * 24)}h` : `${Math.floor(days)}d`
    },
    getSortValue: (row) => row.days_open ?? -1,
  },
  {
    key: 'register_open_time',
    label: 'Opened At',
    type: 'datetime',
    contexts: ['platform'],
  },
]

const prefixIncorrectPlatformColumns: ExceptionColumnDef[] = [
  ...platformCommonColumns,
  {
    key: 'invoice_prefix',
    label: 'Prefix',
    contexts: ['platform'],
  },
]


export const registerLowClosureRateConfig: ExceptionConfig = {
  codes: ['ex-register-low-closure-rate'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...lowClosureRatePlatformColumns],
  defaultSortColumn: 'closure_rate',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      typeof row.closure_rate === 'number'
        ? `${Math.round(row.closure_rate * 100)}% close rate`
        : null,
      typeof row.effective_days === 'number' && row.effective_days > 0
        ? `over ${row.effective_days} days`
        : null,
    ]),
}

export const registerOpenTooLongConfig: ExceptionConfig = {
  codes: ['ex-outlet-ls-register-open-too-long'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...openTooLongPlatformColumns],
  defaultSortColumn: 'days_open',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      typeof row.days_open === 'number' && row.days_open > 0
        ? `open ${Math.round(row.days_open)} days`
        : null,
      row.register_open_time ? `since ${datePart(row.register_open_time)}` : null,
    ]),
}

export const registerPrefixIncorrectConfig: ExceptionConfig = {
  codes: ['ex-outlet-ls-register-prefix-incorrect'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...prefixIncorrectPlatformColumns],
  defaultSortColumn: 'register_name',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      textPart(row.invoice_prefix) && `prefix "${textPart(row.invoice_prefix)}"`,
    ]),
}

